/**
 * Enhanced Footer Component
 * White footer with structure mirroring Framer site
 * Spec-Kit canonical ordering: imports → types → constants → helpers → component
 */

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCountry } from "../contexts/CountryContext";
import { usAppHref } from "../lib/region";
import { currentRegionHref, regionRelativePath } from "../lib/regionLocation";
import RegionSwitcher from "./RegionSwitcher";

const SCROLL_TO_TOP_BEHAVIOR = "auto";

const scrollToTop = () => {
  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const behavior = mediaQuery.matches ? SCROLL_TO_TOP_BEHAVIOR : "smooth";
  window.scrollTo({ top: 0, behavior });
};

// window.location.pathname carries the region prefix ("/sg", "/us/about"); the homepage check
// needs the region-relative path ("/").
const isOnRegionHome = () => regionRelativePath(window.location.pathname) === '/';

/**
 * Landing-page sections. On the regional home the click scrolls; elsewhere it navigates to the
 * home page's anchor (the US pricing section also has its own route, /pricing). The href behind
 * each is a real region-prefixed URL (currentRegionHref), so the link is announced correctly,
 * works without JavaScript and survives the document's <base href="/">.
 */
const scrollToSection = (navigate, sectionId, { route = `/#${sectionId}` } = {}) => {
  if (!isOnRegionHome()) {
    navigate(route);
    return;
  }
  const section =
    document.getElementById(sectionId) ||
    (sectionId === 'faq-section' ? document.querySelector('[data-faq-section]') : null);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    navigate(route);
  }
};

const scrollToFAQ = (navigate) => scrollToSection(navigate, 'faq-section');

const scrollToPricing = (navigate, isUsSite) =>
  scrollToSection(navigate, 'pricing', { route: isUsSite ? '/pricing' : '/#pricing' });

const CONTACT_EMAILS = ["contact@optra-labs.com", "sales@optra-labs.com"];

/** Footer → Product column (display order top to bottom) */
const PRODUCT_LINKS = [
  { label: "Pricing", isPricing: true },
  { label: "Compare", to: "/compare" },
  { label: "For Startups", href: "https://startup.optra-labs.com/" },
  { label: "Login", to: "/login" },
];

const COMPANY_LINKS = [
  // One About page and one blog for the whole site, at the root (outside this region's router).
  { label: "About", href: "/about", sameTab: true },
  { label: "Blog", href: "/blog", sameTab: true },
  { label: "Speak With Us", href: "https://cal.com/optralabs/15min" },
];

const RESOURCES_LINKS = [
  {
    label: "Guides",
    href: "https://docs.optra-labs.com/",
  },
  { label: "FAQ", isFAQ: true },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Service", to: "/terms" },
];

const FOOTER_COLUMNS = [
  { id: "product", title: "Product", links: PRODUCT_LINKS },
  { id: "company", title: "Company", links: COMPANY_LINKS },
  { id: "resources", title: "Legal & Resources", links: RESOURCES_LINKS },
];

/**
 * OPTRA US footer (/us). `appPath` links go to the US product (a separate app at /us/app), so
 * they are plain <a href> built with usAppHref(). No Singapore privacy/terms pages, no Private
 * Access, no Singapore guides. The checker itself is behind sign-in, so the link says so and
 * goes to the sign-in page; "For advisors" goes to the public plan comparison (Advisor tier),
 * not the signed-in advisor workspace.
 */
const US_FOOTER_COLUMNS = [
  {
    id: "product",
    title: "Product",
    links: [
      { label: "Pricing", isPricing: true },
      { label: "Eligibility checker", appPath: "/login" },
      { label: "For advisors", appPath: "/compare" },
      { label: "Sign In", appPath: "/login" },
    ],
  },
  {
    id: "company",
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Blog", to: "/blog" },
      { label: "Speak With Us", href: "https://cal.com/optralabs/15min" },
    ],
  },
  {
    id: "resources",
    title: "Legal & Resources",
    links: [
      { label: "FAQ", isFAQ: true },
      { label: "Trust", appPath: "/trust" },
      { label: "Privacy", appPath: "/privacy" },
      { label: "Terms", appPath: "/terms" },
    ],
  },
];

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/optra-labs/",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/optra.labs/",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
];

const BRAND_TAGLINE =
  "OPTRA Labs helps SMEs quickly understand which funding programmes are worth pursuing before they invest weeks into an application. We bring clarity to how businesses discover, evaluate, and prioritise opportunities.";

const US_BRAND_TAGLINE =
  "OPTRA Labs helps U.S. small businesses understand which funding programs are worth pursuing before they invest weeks in an application. We bring clarity to how businesses discover, evaluate, and prioritize opportunities.";

/** Same one-line disclaimer the US product carries in its own footer. */
const US_DISCLAIMER =
  "Informational screen only. Not affiliated with any government agency; not an SBA size determination or legal advice; grants are never guaranteed and SAM.gov registration is always free.";

/** Cmd/Ctrl/Shift/middle clicks: let the browser open the real href in a new tab. */
const isModifiedClick = (event) =>
  Boolean(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);

/** Small visible qualifier after a label ("sign in", "for existing clients"). */
const renderLink = (link, navigate, isUsSite) => {
  const baseClass =
    "inline text-[14px] leading-none text-gray-600 hover:text-gray-900 transition-colors duration-200";

  if (link.isFAQ || link.isPricing) {
    // The US footer is pinned to "us" by CountryProvider; Singapore falls back to the address bar.
    const region = isUsSite ? "us" : undefined;
    const sectionHref = link.isFAQ
      ? currentRegionHref("/#faq-section", region)
      : currentRegionHref(isUsSite ? "/pricing" : "/#pricing", region);
    return (
      <a
        href={sectionHref}
        data-inline-link=""
        onClick={(e) => {
          if (isModifiedClick(e)) return;
          e.preventDefault();
          if (link.isFAQ) scrollToFAQ(navigate);
          else scrollToPricing(navigate, isUsSite);
        }}
        className={baseClass}
      >
        {link.label}
      </a>
    );
  }

  if (link.appPath) {
    return (
      <a href={usAppHref(link.appPath)} className={baseClass} data-inline-link="">
        {link.label}
      </a>
    );
  }

  if (link.href && link.sameTab) {
    // A root page of this site (About, the blog index): a full navigation in the same tab.
    return (
      <a href={link.href} className={baseClass} data-inline-link="">
        {link.label}
      </a>
    );
  }

  if (link.href) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClass}
        data-inline-link=""
      >
        {link.label}
        <span className="sr-only"> (opens in new tab)</span>
      </a>
    );
  }

  if (link.to) {
    return (
      <Link to={link.to} onClick={scrollToTop} className={baseClass} data-inline-link="">
        {link.label}
      </Link>
    );
  }

  return null;
};

/**
 * Copyright, region links and social icons: the footer's bottom bar. Also used on its own by the
 * region-neutral pages (ChooserFooter: the chooser at "/", /about, /blog) with region="all".
 */
export function FooterBottomBar({ region, spacingClass = "" }) {
  return (
    <div
      className={`border-t border-neutral-200 ${spacingClass} pt-6 md:pt-8 flex flex-col items-center gap-4 md:flex-row md:items-center md:justify-between`}
    >
      <div className="flex flex-col items-center gap-2 md:items-start">
        <p className="text-[12px] font-normal text-gray-700 m-0 text-center md:text-left">
          © 2026 OPTRA Labs Pte. Ltd. All rights reserved.
        </p>
        <RegionSwitcher variant="inline" region={region} />
      </div>
      <div className="flex gap-3 md:gap-4 text-gray-600">
        {SOCIAL_LINKS.map(({ label, href, icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-80 transition-opacity hover:opacity-100 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md"
            aria-label={label}
          >
            {icon}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function EnhancedFooter() {
  const navigate = useNavigate();
  const isUsSite = useCountry().country === "US";
  const columns = isUsSite ? US_FOOTER_COLUMNS : FOOTER_COLUMNS;

  return (
    <footer className="relative w-full bg-white border-t border-gray-200 py-10 md:py-12" data-testid="footer">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-9 md:gap-y-12 lg:grid-cols-[2.5fr_1fr_1fr_1fr] lg:gap-x-14">
          {/* Column 1: Brand / Description — wide first column pushes Product, Company, Legal right */}
          <div
            className="flex flex-col items-start"
            data-testid="footer-col-brand"
          >
            <p className="text-base font-bold tracking-tight m-0 text-[#60A5FA]" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
              OPTRA Labs
            </p>
            <p className="text-[13px] md:text-[14px] leading-snug text-gray-500 m-0 mt-3 w-full lg:w-[350px] max-w-[34rem]">
              {isUsSite ? US_BRAND_TAGLINE : BRAND_TAGLINE}
            </p>
            <div className="mt-3 flex flex-col gap-1">
              {CONTACT_EMAILS.map((email) => (
                <a
                  key={email}
                  href={`mailto:${email}`}
                  className="text-[13px] md:text-[14px] leading-snug m-0 font-medium text-[#1D4ED8] hover:underline"
                  data-inline-link=""
                >
                  {email}
                </a>
              ))}
            </div>
          </div>

          {/* Columns 2–4: Product, Company, Legal & Resources */}
          {columns.map(({ id, title, links }) => (
            <div
              key={id}
              className="flex flex-col items-start"
              data-testid={`footer-col-${id}`}
            >
              <p className="text-xs font-bold uppercase text-[#60A5FA] opacity-50 m-0">
                {title}
              </p>
              <ul className="flex flex-col gap-4 mt-5 m-0 p-0 list-none w-full">
                {links.map((link) => (
                  <li key={`${id}-${link.label}`} className="m-0 p-0">
                    {renderLink(link, navigate, isUsSite)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {isUsSite ? (
          <p
            className="text-[12px] font-normal text-gray-500 leading-snug"
            style={{ margin: "2.75rem 0 0" }}
            data-testid="footer-us-disclaimer"
          >
            {US_DISCLAIMER}
          </p>
        ) : null}

        <FooterBottomBar region={isUsSite ? "us" : undefined} spacingClass={isUsSite ? "mt-6" : "mt-12 md:mt-16"} />
      </div>
    </footer>
  );
}
